import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Breadcrumbs, H5 } from '../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Input, Media } from 'reactstrap';
import DataTable from '../../Common/DataTable';
import { PlusCircle, Edit, Trash2 } from 'react-feather';
import InsuranceCompanyDataModal from './InsuranceCompanyDataModal';
import DeleteConfirmationModal from '../../Common/Modals/DeleteConfirmationModal';
import { confirmDelete, fetchTableData } from '../../../Api/ApiMethods';

const PROTECTED_INSURANCE_COMPANY_NAMES = ['YOK'];

const isProtectedInsuranceCompany = (company) => {
    const name = String(company?.name || '').trim().toLocaleUpperCase('tr-TR');
    const iban = String(company?.ibanNumber || '').trim().toLocaleUpperCase('tr-TR');
    return PROTECTED_INSURANCE_COMPANY_NAMES.includes(name) || PROTECTED_INSURANCE_COMPANY_NAMES.includes(iban);
};

const InsuranceCompanies = () => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearSelectedRows, setClearSelectedRows] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [data, setData] = useState([]); // filtered list
    const [fullData, setFullData] = useState([]); // complete list fetched once
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const PAGE_SIZE = 1000; // load once, filter on the client
    const insuranceCompaniesApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/insurancecompany';

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows.filter(row => !isProtectedInsuranceCompany(row)));
    }, []);

    const applyFilter = useCallback((items, term) => {
        const normalized = term.trim().toLocaleLowerCase('tr-TR');
        if (!normalized) return items;
        return items.filter(item => (item.name || '').toLocaleLowerCase('tr-TR').includes(normalized));
    }, []);

    const handleSearch = (event) => {
        event.preventDefault();
        setSearch(event.target.value);
    };

    const loadCompanies = useCallback(async () => {
        setIsLoading(true);
        try {
            await fetchTableData({
                apiEndpoint: insuranceCompaniesApiUrl,
                page: 1,
                pageSize: PAGE_SIZE,
                setDataCallback: (items) => {
                    setFullData(items);
                    setData(items);
                },
            });
        } finally {
            setIsLoading(false);
        }
    }, [insuranceCompaniesApiUrl, PAGE_SIZE]);

    useEffect(() => {
        loadCompanies();
    }, [loadCompanies]);

    useEffect(() => {
        setData(applyFilter(fullData, search));
    }, [search, fullData, applyFilter]);

    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const openAddModal = () => {
        setModalMode('add');
        setSelectedCompany(null);
        setModalOpen(true);
    };

    const openModifyModal = (company) => {
        setModalMode('modify');
        setSelectedCompany(company);
        setModalOpen(true);
    };

    const columns = [
        { name: 'Şirket İsmi', selector: row => row.name, sortable: true },
        { name: 'Adres', selector: row => row.address, sortable: true },
        { name: 'Telefon Numarası', selector: row => row.phoneNumber, sortable: true },
        { name: 'E-Mail', selector: row => row.email, sortable: true },
        { name: 'İletişim Kişisi', selector: row => row.contactPerson, sortable: true },
        { name: 'Web Sitesi', selector: row => row.website, sortable: true },
        { name: 'Vergi Numarası', selector: row => row.taxNumber, sortable: true },
        { name: 'IBAN Numarası', selector: row => row.ibanNumber, sortable: true },
        { 
            name: 'Tahkime Üye Mi?', 
            selector: row => row.isArbitrationMember ? 'Evet' : 'Hayır', 
            sortable: true,
            center: true,
        },
        {
            name: 'Düzenle',
            cell: (row) => isProtectedInsuranceCompany(row) ? null : (
                    <div
                        className="btn btn-outline-warning"
                        onClick={() => openModifyModal(row)}
                    >
                        <Edit size={18} />
                    </div>
                ),
            ignoreRowClick: true,
            right: true,
            button: true
        },
    ];

    return (
        <Fragment>
            <Breadcrumbs parent='Hazır Girdi Alanları' title='Sigorta Şirketleri' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Sigorta Şirketleri</H5>
                                <span>Tanımlanmış olan sigorta şirketleri aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                    <div className='form-inline me-2 mt-3 p-0 custom-responsive-width'>
                                        <div className='mb-0 form-group d-flex align-items-center border rounded px-2' style={{ height: '35px' }}>
                                            <i className='fa fa-search text-muted'></i>
                                            <Input
                                                type="text"
                                                placeholder="Ara.."
                                                value={search}
                                                onChange={(e) => handleSearch(e)}
                                                className="border-0 ms-2 shadow-none"
                                                style={{ height: '33px', flexGrow: 1 }}
                                            />
                                        </div>
                                    </div>
                                    <Media body className='text-end mt-3'>
                                        <div className="btn btn-outline-primary ms-2" onClick={openAddModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <PlusCircle size={18} className="me-1"/>
                                            {' Sigorta Şirketi Ekle'}
                                        </div>
                                    </Media>
                                    {(selectedRows.length !== 0) &&
                                        <div className='btn btn-outline-danger ms-2 mt-3' onClick={toggleDeleteModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <Trash2 size={18} className="me-1"/>
                                            {' Seçilenleri Sil'}
                                        </div>
                                    }
                                </div>

                                <div style={{ height: "40px" }}></div>

                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        onSelectedRowsChange={handleRowSelected}
                                        pagination
                                        selectableRows
                                        selectableRowDisabled={isProtectedInsuranceCompany}
                                        clearSelectedRows={clearSelectedRows}
                                        progressPending={isLoading}
                                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                        noDataComponent={isLoading ? null : <div className="p-4 text-center">Sigorta şirketi bulunamadı.</div>}
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <InsuranceCompanyDataModal
                apiEndpoint={insuranceCompaniesApiUrl}
                isOpen={modalOpen}
                toggle={() => setModalOpen(!modalOpen)}
                mode={modalMode}
                selectedCompany={selectedCompany}
                refreshData={loadCompanies}
            />
            <DeleteConfirmationModal
                isOpen={deleteModal}
                toggle={toggleDeleteModal}
                itemsToDelete={selectedRows}
                displayKey="name"
                onConfirm={() =>
                    confirmDelete({
                        apiEndpoint: insuranceCompaniesApiUrl,
                        selectedItems: selectedRows,
                        toggleModalCallback: toggleDeleteModal,
                        clearSelectionCallback: () => {
                            setSelectedRows([]);
                            setClearSelectedRows(!clearSelectedRows);
                        },
                        refreshDataCallback: loadCompanies,
                    })
                }
            />
        </Fragment>
    );
};

export default InsuranceCompanies;
